import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, GitBranch } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetRepositoriesQuery,
  useCreateRepositoryMutation,
  useUpdateRepositoryMutation,
  useDeleteRepositoryMutation,
} from '@/store/api/apiSlice';
import { asList } from '@/store/api/daytonaApi';
import type { Repository } from '@/types';
import { format } from 'date-fns';

const RESOURCE_PRESETS = [
  { label: 'Small (2 CPU / 4 GB / 10 GB)', cpu: 2, memory: 4, disk: 10 },
  { label: 'Medium (4 CPU / 8 GB / 20 GB)', cpu: 4, memory: 8, disk: 20 },
  { label: 'Large (8 CPU / 16 GB / 40 GB)', cpu: 8, memory: 16, disk: 40 },
];

interface FormData {
  name: string;
  gitUrl: string;
  gitAuthType: 'https' | 'ssh';
  credentials: string;
  resourcePreset: number;
  indexerImage: string;
}

const defaultFormData: FormData = {
  name: '',
  gitUrl: '',
  gitAuthType: 'https',
  credentials: '',
  resourcePreset: 0,
  indexerImage: '',
};

export function RepositoriesPage() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRepo, setEditingRepo] = useState<Repository | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Repository | null>(null);
  const [formData, setFormData] = useState<FormData>(defaultFormData);

  const { data: repositories, isLoading } = useGetRepositoriesQuery();
  const [createRepository, { isLoading: isCreating }] = useCreateRepositoryMutation();
  const [updateRepository, { isLoading: isUpdating }] = useUpdateRepositoryMutation();
  const [deleteRepository, { isLoading: isDeleting }] = useDeleteRepositoryMutation();

  const repoList = asList<Repository>(repositories);

  const columns: ColumnDef<Repository, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'gitUrl',
      header: 'Git URL',
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">{row.original.gitUrl}</span>
      ),
    },
    {
      accessorKey: 'gitAuthType',
      header: 'Auth',
      cell: ({ row }) => <StatusBadge status={row.original.gitAuthType} />,
    },
    {
      id: 'branches',
      header: 'Branches',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.branches?.length ?? 0}</span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge status={row.original.isActive ? 'active' : 'stopped'} />
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  function openCreate() {
    setEditingRepo(null);
    setFormData(defaultFormData);
    setModalOpen(true);
  }

  function openEdit(repo: Repository) {
    setEditingRepo(repo);
    const presetIdx = RESOURCE_PRESETS.findIndex(
      (p) =>
        repo.daytonaResources &&
        p.cpu === repo.daytonaResources.cpu &&
        p.memory === repo.daytonaResources.memory &&
        p.disk === repo.daytonaResources.disk,
    );
    setFormData({
      name: repo.name,
      gitUrl: repo.gitUrl,
      gitAuthType: repo.gitAuthType,
      credentials: '',
      resourcePreset: presetIdx >= 0 ? presetIdx : 0,
      indexerImage: repo.indexerImage ?? '',
    });
    setModalOpen(true);
  }

  async function handleSave() {
    const preset = RESOURCE_PRESETS[formData.resourcePreset];
    const payload = {
      name: formData.name,
      gitAuthType: formData.gitAuthType,
      indexerImage: formData.indexerImage || undefined,
      daytonaResources: preset
        ? { cpu: preset.cpu, memory: preset.memory, disk: preset.disk }
        : undefined,
      ...(formData.credentials ? { credentials: formData.credentials } : {}),
    };

    if (editingRepo) {
      await updateRepository({ id: editingRepo._id, body: payload }).unwrap();
    } else {
      await createRepository({ ...payload, gitUrl: formData.gitUrl }).unwrap();
    }
    setModalOpen(false);
  }

  async function handleDelete() {
    if (deleteTarget) {
      await deleteRepository(deleteTarget._id).unwrap();
      setDeleteTarget(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Repositories</h1>
          <p className="text-sm text-muted-foreground">
            Manage indexed repositories and their branches
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Repository
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <p className="text-sm text-muted-foreground">
            Click a repository row to manage its branches
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : repoList.length === 0 ? (
            <EmptyState
              title="No repositories found"
              description="Add a repository to get started with indexing"
              action={
                <Button onClick={openCreate}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Repository
                </Button>
              }
            />
          ) : (
            <DataTable
              columns={columns}
              data={repoList}
              onRowClick={(row) => navigate(`/repositories/${row._id}/branches`)}
            />
          )}
        </CardContent>
      </Card>

      {/* Create/Edit modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRepo ? 'Edit Repository' : 'Add Repository'}</DialogTitle>
            <DialogDescription>
              {editingRepo
                ? 'Update repository configuration below.'
                : 'Configure a new repository for indexing.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="my-repo"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            {!editingRepo && (
              <div className="space-y-2">
                <Label htmlFor="gitUrl">Git URL</Label>
                <Input
                  id="gitUrl"
                  placeholder="https://github.com/org/repo.git"
                  value={formData.gitUrl}
                  onChange={(e) => setFormData({ ...formData, gitUrl: e.target.value })}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="gitAuthType">Auth Type</Label>
              <select
                id="gitAuthType"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.gitAuthType}
                onChange={(e) =>
                  setFormData({ ...formData, gitAuthType: e.target.value as 'https' | 'ssh' })
                }
              >
                <option value="https">HTTPS</option>
                <option value="ssh">SSH</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="credentials">Credentials (optional)</Label>
              <textarea
                id="credentials"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder={
                  formData.gitAuthType === 'ssh'
                    ? 'Paste SSH private key...'
                    : 'Username:token or access token'
                }
                value={formData.credentials}
                onChange={(e) => setFormData({ ...formData, credentials: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resourcePreset">Daytona Resources</Label>
              <select
                id="resourcePreset"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.resourcePreset}
                onChange={(e) =>
                  setFormData({ ...formData, resourcePreset: parseInt(e.target.value) })
                }
              >
                {RESOURCE_PRESETS.map((preset, idx) => (
                  <option key={idx} value={idx}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="indexerImage">Custom Indexer Image (optional)</Label>
              <Input
                id="indexerImage"
                placeholder="ghcr.io/org/indexer:latest"
                value={formData.indexerImage}
                onChange={(e) => setFormData({ ...formData, indexerImage: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isCreating || isUpdating}>
              {isCreating || isUpdating ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Repository"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This will delete all branches, Neon projects, and Daytona sandboxes. This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
